import { pages, subPackages, tabBar } from '@/pages.json'
import { isH5, isMp } from './platform'

const getLastPage = () => {
  // getCurrentPages() 至少有1个元素，所以不再额外判断
  // const lastPage = getCurrentPages().at(-1)
  // 上面那个在低版本安卓中打包回报错，所以改用下面这个【虽然我加了src/interceptions/prototype.ts，但依然报错】
  const currentPages = getCurrentPages()
  return currentPages[currentPages.length - 1]
}

/** 判断当前页面是否是tabbar页  */
export const getIsTabBar = (detectionPath?: string) => {
  if (!tabBar) {
    return false
  }
  if (!tabBar.list.length) {
    // 通常有tabBar的话，list不能有空，且至少有2个元素，这里其实不用处理
    return false
  }
  const lastPage = getLastPage()
  const currPath = detectionPath || lastPage.route
  return !!tabBar.list.find((e) => {
    return '/' + e.pagePath === currPath
  })
}

/**
 * 获取当前页面路由的 path 路径和 redirectPath 路径
 * path 如 ‘/page/login/index’
 * redirectPath 如 ‘/page/serviceChat/base/route-interceptor’
 */
export const currRoute = () => {
  const lastPage = getLastPage()
  const currRoute = (lastPage as any).$page
  const { fullPath } = currRoute as { fullPath: string }
  return getUrlObj(fullPath)
}

const ensureDecodeURIComponent = (url: string) => {
  if (url.startsWith('%')) {
    return ensureDecodeURIComponent(decodeURIComponent(url))
  }
  return url
}
/**
 * 解析 url 得到 path 和 query
 * 比如输入url: /page/login/index?redirect=%2Fpages%2Fdemo%2Fbase%2Froute-interceptor
 * 输出: {path: /page/login/index, query: {redirect: /page/serviceChat/base/route-interceptor}}
 */
export const getUrlObj = (url: string) => {
  const [path, queryStr] = url.split('?')
  if (!queryStr) {
    return {
      path,
      query: {},
    }
  }
  const query: Record<string, string> = {}
  queryStr.split('&').forEach((item) => {
    const [key, value] = item.split('=')
    // console.log(key, value)
    query[key] = ensureDecodeURIComponent(value) // 这里需要统一 decodeURIComponent 一下，可以兼容h5和微信y
  })
  return { path, query }
}
/**
 * 得到所有的需要登录的pages，包括主包和分包的
 * 这里设计得通用一点，可以传递key作为判断依据，默认是 needLogin, 与 route-block 配对使用
 * 如果没有传 key，则表示所有的pages，如果传递了 key, 则表示通过 key 过滤
 */
export const getAllPages = (key = 'needLogin') => {
  // 这里处理主包
  const mainPages = [
    ...pages
      .filter((page) => !key || page[key])
      .map((page) => ({
        ...page,
        path: `/${page.path}`,
      })),
  ]
  // 这里处理分包
  const subPages: any[] = []
  subPackages.forEach((subPageObj) => {
    const { root } = subPageObj
    subPageObj.pages
      .filter((page) => !key || page[key])
      .forEach((page: { path: string } & Record<string, any>) => {
        subPages.push({
          ...page,
          path: `/${root}/${page.path}`,
        })
      })
  })
  const result = [...mainPages, ...subPages]
  // console.log(`getAllPages by ${key} result: `, result)
  return result
}

/**
 * 得到所有的需要登录的pages，包括主包和分包的
 * 只得到 path 数组
 */
export const getNeedLoginPages = (): string[] => getAllPages('needLogin').map((page) => page.path)

/**
 * 得到所有的需要登录的pages，包括主包和分包的
 * 只得到 path 数组
 */
export const needLoginPages: string[] = getAllPages('needLogin').map((page) => page.path)

/**
 * 根据微信小程序当前环境，判断应该获取的BaseUrl
 */
export const getEvnBaseUrl = () => {
  // 请求基准地址
  const isDev = import.meta.env.DEV
  let baseUrl = import.meta.env.VITE_SERVER_BASEURL

  if (isH5) {
    baseUrl = isDev ? '/api' : import.meta.env.VITE_SERVER_BASEURL
  }
  // 小程序端环境区分
  if (isMp) {
    const {
      miniProgram: { envVersion },
    } = uni.getAccountInfoSync()

    switch (envVersion) {
      case 'develop':
        baseUrl = 'https://testapi.beijingzxj.com'
        break
      case 'trial':
        baseUrl = 'https://testapi.beijingzxj.com'
        break
      case 'release':
        baseUrl = 'https://api.beijingzxj.com'
        break
    }
  }
  return baseUrl
}

/**
 * 根据微信小程序当前环境，判断应该获取的BaseUrl
 */
export const getEvnBaiDuBaseUrl = () => {
  // 请求基准地址
  const isDev = import.meta.env.DEV
  let baseUrl = ''

  if (isH5) {
    baseUrl = '/baidubce'
  }
  // 小程序端环境区分
  if (isMp) {
    baseUrl = 'https://aip.baidubce.com/'
  }
  return baseUrl
}

/**
 * 获取系统信息
 */
export const getSysInfo = () => {
  const sys = uni.getSystemInfoSync()
  return {
    screenHeight: sys.screenHeight, // 屏幕高度
    windowHeight: sys.screenHeight, // 可使用窗口高度
    statusBarHeight: sys.statusBarHeight, // 手机状态栏的高度
    safeArea: sys.safeArea, // 安全区域高度
  }
}

/**
 * 获取节点信息
 * @param selector 节点选择器 #id,.class
 * @param all 是否返回所有 selector 对应的节点
 */
export function getRect<T extends boolean>(
  selector: string,
  all: T,
  scope?: any,
): Promise<RectResultType<T>> {
  return new Promise<RectResultType<T>>((resolve, reject) => {
    let query: UniNamespace.SelectorQuery | null = null
    if (scope) {
      query = uni.createSelectorQuery().in(scope)
    } else {
      query = uni.createSelectorQuery()
    }
    query[all ? 'selectAll' : 'select'](selector)
      .boundingClientRect((rect) => {
        if (all && isArray(rect) && rect.length > 0) {
          resolve(rect as RectResultType<T>)
        } else if (!all && rect) {
          resolve(rect as RectResultType<T>)
        } else {
          reject(new Error('No nodes found'))
        }
      })
      .exec()
  })
}

// 姓名脱敏
export const userNameDesensitization = (name: string) => {
  if (!name) {
    return '*'
  }
  return '*' + name.charAt(name.length - 1)
}
